import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import '@hotmart/cosmos/dist/time-picker';
import { useEffect, useMemo, useRef } from 'react';
import { CustomIcon } from '../Icon';
import { TimePickerStyles, TimePickerBox } from './styles';

export type TimePickerProps = {
  onChange: (e: CustomEvent) => void;
  label?: string;
  icon?: IconDefinition;
  placeholder?: string;
  time?: string;
  minTime?: Date | string;
  time24hr?: boolean;
  disabled?: boolean;
  invalid?: boolean;
};

const TimePicker = ({
  onChange,
  label,
  icon,
  placeholder,
  time,
  minTime,
  time24hr = false,
  disabled = false,
  invalid = false
}: TimePickerProps) => {
  const timePickerRef = useRef<HTMLElement>(null);
  const changeHandler = (e: Event) => onChange(e as CustomEvent);

  const customPickerProps = useMemo(
    () => ({
      time24hr: time24hr || undefined,
      time: time || undefined,
      disabled: disabled || undefined,
      invalid: invalid || undefined
    }),
    [time24hr, time, disabled, invalid]
  );

  useEffect(() => {
    const timePicker = document.querySelector('hot-time-picker');
    timePicker?.addEventListener('change', changeHandler);
    const shadowRoot = timePicker?.shadowRoot;
    const styles = document.createElement('style');
    styles.prepend(TimePickerStyles.toString());
    shadowRoot?.prepend(styles);

    return () => {
      timePicker?.removeEventListener('change', changeHandler);
    };
  }, [onChange]);

  useEffect(() => {
    if (minTime) {
      (timePickerRef.current as any)?.flatPickr?.setDate(minTime, true, 'H:i');
    }

    (timePickerRef.current as any)?.flatPickr?.set('minTime', minTime);
  }, [minTime, timePickerRef]);

  return (
    <>
      {label ? <label htmlFor="time-picker">{label}</label> : null}
      <TimePickerBox className="_position-relative _rounded _border-gray-200">
        {icon ? (
          <div
            tabIndex={-1}
            className="_position-absolute icon-box _d-flex _align-items-center _mr-4"
          >
            <span className="_sr-only">icon</span>
            <CustomIcon className="_text-gray-500" icon={icon} />
          </div>
        ) : null}
        <hot-time-picker
          className="_border _border-gray-200"
          ref={timePickerRef}
          placeholder={placeholder}
          {...customPickerProps}
        ></hot-time-picker>
      </TimePickerBox>
    </>
  );
};

export default TimePicker;
